/**
 * Davet Kabul — Spec 1.5.T8 + S2.T5b
 *
 *   GET  /api/public/invitation/:token        → preview
 *   POST /api/public/invitation/:token/accept → kabul et
 *
 * Akış:
 *   - Preview göster: hangi firma, hangi rol, kim davet etti
 *   - Eğer requiresSignUp:false → "Giriş Yap + Kabul Et" (auth varsa direkt accept)
 *   - Eğer requiresSignUp:true → name + password + KVKK formu → accept
 */
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AcceptInvitationSchema,
  type AcceptInvitationInput,
  PasswordSchema,
} from '@swskills/shared';
import { Header } from '../components/Header.js';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
} from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';
import { z } from 'zod';

interface InvitationPreview {
  invitation: {
    email: string;
    role: 'OWNER' | 'ADMIN' | 'VIEWER';
    expiresAt: string;
    organization: { id: string; name: string; industry: string };
    invitedBy: { name: string | null; email: string } | null;
  };
  requiresSignUp: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  VIEWER: 'Görüntüleyici',
};

// Sign-up formu için ayrı schema (token URL'den gelir)
const SignupFormSchema = z.object({
  password: PasswordSchema,
  name: z.string().min(2, 'İsim en az 2 karakter').max(100),
  kvkkAydinlatmaAccept: z.literal(true, {
    errorMap: () => ({ message: 'Aydınlatma onayı zorunlu' }),
  }),
});
type SignupFormInput = z.infer<typeof SignupFormSchema>;

export function InvitationAcceptPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  const { data: session, isPending: sessionPending } = useSession();
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<InvitationPreview>(
          `/api/public/invitation/${token}`,
        );
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setPreviewError(err.message);
        else setPreviewError('Davet yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormInput>({
    resolver: zodResolver(SignupFormSchema),
    defaultValues: {
      password: '',
      name: '',
      kvkkAydinlatmaAccept: false as unknown as true,
    },
  });

  async function acceptExisting(): Promise<void> {
    setServerError(null);
    setAccepting(true);
    try {
      await api.post(`/api/public/invitation/${token}/accept`, {});
      setLocation('/yonetici/dashboard');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'FORBIDDEN' && err.message.includes('Doğru hesapla')) {
          setServerError(
            `Bu davet ${preview?.invitation.email} adresine gönderilmiş. Şu an farklı bir hesapla girişlisiniz. Lütfen oturum kapatıp tekrar deneyin.`,
          );
        } else {
          setServerError(err.message);
        }
      } else {
        setServerError('Davet kabul edilemedi');
      }
      setAccepting(false);
    }
  }

  async function acceptNew(values: SignupFormInput): Promise<void> {
    setServerError(null);
    try {
      const payload: AcceptInvitationInput = {
        token,
        password: values.password,
        name: values.name,
        kvkkAydinlatmaAccept: values.kvkkAydinlatmaAccept,
      };
      AcceptInvitationSchema.parse(payload);
      await api.post(`/api/public/invitation/${token}/accept`, payload);
      // Yeni kullanıcı yaratıldı + member yapıldı. Email henüz doğrulanmamış olabilir
      // → giriş sayfasına yönlendir, mesaj göster
      setLocation(
        `/yonetici/giris?invitation_accepted=1&email=${encodeURIComponent(
          preview?.invitation.email ?? '',
        )}`,
      );
    } catch (err) {
      if (err instanceof ApiError) setServerError(err.message);
      else setServerError('Davet kabul edilemedi');
    }
  }

  if (previewError) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-md px-6 py-12">
          <Card>
            <Alert variant="danger">{previewError}</Alert>
            <Button
              variant="secondary"
              onClick={() => setLocation('/')}
              className="mt-4 w-full"
            >
              Ana Sayfa
            </Button>
          </Card>
        </main>
      </div>
    );
  }

  if (!preview || sessionPending) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-md px-6 py-12">
          <p className="text-slate-500">Davet yükleniyor…</p>
        </main>
      </div>
    );
  }

  const { invitation, requiresSignUp } = preview;
  const inviter =
    invitation.invitedBy?.name ?? invitation.invitedBy?.email ?? 'Yönetici';
  const isLoggedInWithSameEmail =
    !!session?.user && session.user.email === invitation.email;
  const isLoggedInWithDifferentEmail =
    !!session?.user && session.user.email !== invitation.email;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-md px-6 py-12">
        <Card>
          <h1 className="text-2xl font-bold text-slate-900">
            Davet Aldınız
          </h1>
          <p className="mt-2 text-slate-700">
            <strong>{inviter}</strong>,{' '}
            <strong>{invitation.organization.name}</strong> firmasına sizi{' '}
            <strong>{ROLE_LABELS[invitation.role]}</strong> olarak davet etti.
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Bu davet {invitation.email} adresine gönderildi.
          </p>

          {serverError && (
            <div className="mt-4">
              <Alert variant="danger">{serverError}</Alert>
            </div>
          )}

          {isLoggedInWithDifferentEmail && (
            <div className="mt-4">
              <Alert variant="warning">
                <strong>Farklı hesapla girişlisiniz.</strong> Bu davet{' '}
                {invitation.email} adresine. Önce çıkış yapın, sonra daveti
                kabul edin.
              </Alert>
            </div>
          )}

          {/* Mevcut hesap kullanıcı — auth varsa direkt accept */}
          {!requiresSignUp && (
            <div className="mt-6">
              {isLoggedInWithSameEmail ? (
                <Button
                  onClick={acceptExisting}
                  loading={accepting}
                  className="w-full"
                >
                  Daveti Kabul Et
                </Button>
              ) : (
                <>
                  <p className="mb-3 text-sm text-slate-700">
                    Bu e-posta için zaten bir hesabınız var. Önce giriş yapın.
                  </p>
                  <Button
                    onClick={() =>
                      setLocation(
                        `/yonetici/giris?next=${encodeURIComponent(
                          `/davet/${token}`,
                        )}&email=${encodeURIComponent(invitation.email)}`,
                      )
                    }
                    className="w-full"
                  >
                    Giriş Yap
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Yeni kullanıcı kayıt formu */}
          {requiresSignUp && (
            <form
              onSubmit={handleSubmit(acceptNew)}
              className="mt-6"
              noValidate
            >
              <Field
                label="Ad Soyad"
                htmlFor="signup-name"
                required
                error={errors.name?.message}
              >
                <Input
                  id="signup-name"
                  type="text"
                  autoComplete="name"
                  error={!!errors.name}
                  {...register('name')}
                />
              </Field>

              <Field
                label="Şifre"
                htmlFor="signup-password"
                required
                error={errors.password?.message}
                hint="En az 8 karakter, 1 büyük + 1 küçük + 1 rakam"
              >
                <Input
                  id="signup-password"
                  type="password"
                  autoComplete="new-password"
                  error={!!errors.password}
                  {...register('password')}
                />
              </Field>

              <div className="mt-4">
                <Checkbox
                  id="signup-kvkk"
                  label={
                    <span>
                      <a
                        href="/kvkk"
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-900 underline"
                      >
                        Aydınlatma Metni
                      </a>
                      'ni okudum, kişisel verilerimin işlenmesini onaylıyorum.{' '}
                      <span className="text-red-500">*</span>
                    </span>
                  }
                  error={errors.kvkkAydinlatmaAccept?.message}
                  {...register('kvkkAydinlatmaAccept')}
                />
              </div>

              <Button type="submit" loading={isSubmitting} className="mt-4 w-full">
                Hesabı Oluştur ve Kabul Et
              </Button>
            </form>
          )}
        </Card>
      </main>
    </div>
  );
}
